import React, { useMemo, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { pageUrls } from 'globalconfig/constants/keys';
import { getAuthDetails } from 'globalutils/fmUtils';
import RenderComponent from './RenderComponent';
import { getConfig, authValidator } from './utils';
import CautionIcon from 'globalcomponents/CautionIcon';

export default function useAuthGuard({ history, location, folderPath, setAuth }) {
  const auth = getAuthDetails();
  const ADMIN = '/admin/';
  const ADMIN_LOGIN = '/admin/login';
  const WEB_LOGIN = pageUrls.login;
  const url = folderPath === ADMIN ? ADMIN_LOGIN : WEB_LOGIN;

  const [filterPopupOpen, setFilterPopupOpen] = useState(false);

  const setFilterPopup = () => {
    setFilterPopupOpen(true);
  };

  const basePath = `.${folderPath}${window?.$environment?.SERVICE_PROVIDER}`;

  const { routeConfigs, tenantWiseLogic } = useMemo(() => {
    return {
      routeConfigs: getConfig(basePath, 'privateConfig'),
      tenantWiseLogic: getConfig(basePath, 'tenantWiseLogic'),
    };
  }, []);

  const renderComponent = (config, customConfig = {}, additionalProps = {}) => (
    <Route
      exact
      path={config.url}
      key={config.url}
      render={() => (
        <RenderComponent
          history={history}
          setAuth={setAuth}
          config={{ ...config, ...customConfig }}
          setFilterPopup={setFilterPopup}
          folderPath={folderPath}
          {...additionalProps}
        />
      )}
    />
  );

  const tenantLogics = tenantWiseLogic?.({
    history,
    auth,
    location,
    folderPath,
    renderComponent,
  });

  const renderPermissionDenied = (config, additionalProps = {}) =>
    renderComponent(
      config,
      { title: 'Permission Denied', component: 'Unhandled' },
      {
        message: () => (
          <div className="PermissionChanges">
            <CautionIcon noButton message={'Permission Denied'} />
          </div>
        ),
        permissionDenied: true,
        ...additionalProps,
      }
    );

  const privateRotes = routeConfigs?.map((config) => {
    if (location.state?.error === 'permission denied') {
      return renderPermissionDenied(config, {});
    } else if (auth?.auth && (tenantLogics?.authCheck?.({ config: {} }) ?? true)) {
      const { hasRolePermission, isTierEnabled } = authValidator(config);
      // if has auth
      // admin suspended case, only user/profile is accessible
      if (auth.adminSuspended && (tenantLogics?.adminSuspended?.({ config, renderComponent }) ?? true)) {
        return config.url === '/user/profile'
          ? renderComponent(config)
          : renderComponent(
              config,
              {
                title: config.title,
                component: 'Unhandled',
                layout: 'authenticatedStyle',
              },
              { isPopUp: true }
            );
      }
      // if subscription not enabled
      else if (!isTierEnabled && auth?.showUpgradePopup && (tenantLogics?.hasSubscription?.({ config }) ?? true)) {
        return renderComponent(config, { title: 'Upgrade Subscription', component: 'SubscriptionMessage' });
      } else if (!isTierEnabled && auth?.showUpgradePopup === false) {
        return renderPermissionDenied(config, {});
      } else if (hasRolePermission && (tenantLogics?.roleCheck?.({ config }) ?? true)) {
        return renderComponent(config, {}, {}, { layout: config.layout });
      } else {
        return renderPermissionDenied(config, {});
      }
    } else {
      // if not authenticated
      return (
        <Redirect
          to={{
            pathname: url,
            ...(location?.search
              ? {
                  search: `redirect=${location?.pathname}${location?.search}`,
                }
              : {}),
            state: {
              ...location.state,
              referrer: url !== location.pathname ? location.pathname : '',
              search: location.pathname !== pageUrls.resetPassword ? location.search : '',
            },
          }}
          key={url}
        />
      );
    }
  });

  return { privateRotes, filterPopupOpen, setFilterPopupOpen };
}
